/*
 * Copyright Mimic Networks, Inc. 2026.
 */

/**
 * Message types for iframe-parent communication in embedded apps.
 * Used for route synchronization between dashboard and embedded iframes.
 */

/** Messages sent FROM iframe TO parent */
export type EmbeddedAppMessageFromIframe =
  | { type: 'READY' }
  | { type: 'ROUTE_CHANGE'; params: Record<string, string> }
  | { type: 'RESIZE'; height: number }
  | { type: 'NAVIGATE'; params: Record<string, string> }
  | { type: 'FULLSCREEN_REQUEST'; fullscreen: boolean }
  | { type: 'DRAWER_OPEN'; eventId: string; eventTs: string }
  | { type: 'DRAWER_CLOSE' };

/** Messages sent FROM parent TO iframe */
export type EmbeddedAppMessageToIframe =
  | { type: 'RESTORE_STATE'; params: Record<string, string> }
  | { type: 'FULLSCREEN_STATE'; fullscreen: boolean }
  | { type: 'DRAWER_CLOSE' };

/** All embedded app messages (union of both directions) */
export type EmbeddedAppMessage = EmbeddedAppMessageFromIframe | EmbeddedAppMessageToIframe;

/**
 * Configuration for an embedded app iframe.
 */
export interface EmbeddedAppConfig {
  /** Display name for debugging/errors */
  name: string;
  /** Function to compute iframe src URL given env and optional params */
  getUrl: (env: string, params?: Record<string, string>) => string;
  /** Allowed origins for postMessage validation */
  getAllowedOrigins: (env: string) => string[];
}

/**
 * Validates that an object is a plain object with all string values.
 * Uses cross-realm safe check since postMessage objects come from iframe's JS realm.
 */
function isStringRecord(obj: unknown): obj is Record<string, string> {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return false;
  }

  // Cross-realm safe plain object check (works for objects from iframe)
  if (Object.prototype.toString.call(obj) !== '[object Object]') {
    return false;
  }

  return Object.values(obj as Record<string, unknown>).every((v) => typeof v === 'string');
}

/**
 * Type guard to validate incoming postMessage data is a valid embedded app message.
 * Only validates messages from iframe (READY, ROUTE_CHANGE).
 */
export function isEmbeddedAppMessageFromIframe(data: unknown): data is EmbeddedAppMessageFromIframe {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const msg = data as Record<string, unknown>;

  if (msg.type === 'READY') {
    return true;
  }

  if (msg.type === 'ROUTE_CHANGE') {
    return isStringRecord(msg.params);
  }

  if (msg.type === 'RESIZE') {
    return Number.isFinite(msg.height) && (msg.height as number) >= 0;
  }

  if (msg.type === 'NAVIGATE') {
    const params = msg.params as Record<string, unknown> | undefined;
    const path = msg.path ?? params?.path;
    return typeof path === 'string' && path.startsWith('/');
  }

  if (msg.type === 'FULLSCREEN_REQUEST') {
    return typeof msg.fullscreen === 'boolean';
  }

  if (msg.type === 'DRAWER_OPEN') {
    return typeof msg.eventId === 'string' && typeof msg.eventTs === 'string';
  }

  if (msg.type === 'DRAWER_CLOSE') {
    return true;
  }

  return false;
}
